// src/pages/AdmissionsPage.tsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Heading,
  HStack,
  Stack,
  Select,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useListAdmissions } from "../apis/patients";
import { useDischargeAdmission } from "../apis/patients";
import DischargeConfirm from "../features/admissions/DischargeConfirm";

// ---- Minimal local type to help TS (only fields we render)
type AdmissionRow = {
  _id: string;
  patientName?: string;
  patientId: string;
  status: "admitted" | "discharged" | "transferred" | string;
  admittedAt?: string;
  createdAt?: string;
  dischargedAt?: string;
  ward?: string;
  bed?: string;
};

// ---- Safe formatter (avoids "Invalid Date")
const fmtDate = (iso?: string) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

export default function AdmissionsPage() {
  const [status, setStatus] = useState<
    "admitted" | "discharged" | "transferred" | ""
  >("admitted");

  const location = useLocation();

  // Read ?patientId=... from the URL if present
  const searchParams = new URLSearchParams(location.search);
  const initialPatientId = searchParams.get("patientId") || "";

  const [patientId, setPatientId] = useState<string>(initialPatientId);

  const params = useMemo(
    () => ({
      status: (status || undefined) as any,
      patientId: patientId || undefined,
    }),
    [status, patientId]
  );

  const { data, isLoading, refetch } = useListAdmissions(params);

  const rows: AdmissionRow[] = Array.isArray(data)
    ? (data as AdmissionRow[])
    : [];

  const [dischargeTarget, setDischargeTarget] = useState<{
    id: string;
    patientId: string;
  } | null>(null);

  const navigate = useNavigate();
  // you still import useDischargeAdmission, leaving logic as-is
  useDischargeAdmission(undefined); // to avoid unused import lint – no behavior change

  const handleOpenDischarge = (id: string, pId: string) => {
    setDischargeTarget({ id, patientId: pId });
  };

  return (
    <Box p={{ base: 3, md: 4 }}>
      <Heading size="md" mb={4}>
        Admissions
      </Heading>

      {/* Filters – responsive layout */}
      <Stack
        direction={{ base: "column", md: "row" }}
        spacing={3}
        mb={4}
        align={{ base: "stretch", md: "center" }}
      >
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as any)}
          width={{ base: "100%", md: "220px" }}
        >
          <option value="">All statuses</option>
          <option value="admitted">Admitted</option>
          <option value="discharged">Discharged</option>
          <option value="transferred">Transferred</option>
        </Select>

        <Input
          placeholder="Filter by Patient ID (optional)"
          value={patientId}
          onChange={(e) => setPatientId(e.target.value.trim())}
          width={{ base: "100%", md: "320px" }}
        />

        <Box>
          <Button
            width={{ base: "100%", md: "auto" }}
            onClick={() => refetch()}
          >
            Apply
          </Button>
        </Box>
      </Stack>

      {isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No admissions found for the current filters.
        </Text>
      ) : (
        <>
          {/* Desktop / tablet: table view */}
          <Box display={{ base: "none", md: "block" }}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Patient Name</Th>
                  <Th>Patient ID</Th>
                  <Th>Status</Th>
                  <Th>Admitted At</Th>
                  <Th>Discharged At</Th>
                  <Th>Ward/Bed</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((a) => {
                  const admittedAt = a.admittedAt ?? a.createdAt;
                  return (
                    <Tr key={a._id}>
                      <Td>
                        <Link to={`/patients/${a.patientId}`}>
                          {a.patientName ?? "—"}
                        </Link>
                      </Td>
                      <Td>{a.patientId}</Td>
                      <Td>
                        <Badge
                          colorScheme={
                            a.status === "admitted"
                              ? "green"
                              : a.status === "discharged"
                              ? "gray"
                              : "yellow"
                          }
                        >
                          {a.status}
                        </Badge>
                      </Td>
                      <Td>{fmtDate(admittedAt)}</Td>
                      <Td>{fmtDate(a.dischargedAt)}</Td>
                      <Td>
                        {[a.ward, a.bed].filter(Boolean).join(" / ") || "-"}
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => navigate(`/admissions/${a._id}`)}
                          >
                            View
                          </Button>
                          <Button
                            size="xs"
                            onClick={() =>
                              navigate(`/appointments?patient=${a.patientId}`)
                            }
                          >
                            Open Appointment(s)
                          </Button>

                          {a.status === "admitted" && (
                            <Button
                              size="xs"
                              colorScheme="red"
                              onClick={() =>
                                handleOpenDischarge(a._id, a.patientId)
                              }
                            >
                              Discharge
                            </Button>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {/* Mobile: card list view */}
          <Box display={{ base: "block", md: "none" }}>
            <Stack spacing={3}>
              {rows.map((a) => {
                const admittedAt = a.admittedAt ?? a.createdAt;

                return (
                  <Box
                    key={a._id}
                    borderWidth="1px"
                    borderRadius="md"
                    p={3}
                    bg="white"
                    boxShadow="sm"
                  >
                    <HStack justify="space-between" mb={1}>
                      <Box>
                        <Text
                          fontWeight="bold"
                          fontSize="sm"
                          noOfLines={1}
                          maxW="180px"
                        >
                          <Link to={`/patients/${a.patientId}`}>
                            {a.patientName ?? "—"}
                          </Link>
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          ID: {a.patientId}
                        </Text>
                      </Box>
                      <Badge
                        colorScheme={
                          a.status === "admitted"
                            ? "green"
                            : a.status === "discharged"
                            ? "gray"
                            : "yellow"
                        }
                      >
                        {a.status}
                      </Badge>
                    </HStack>

                    <Box mt={1}>
                      <Text fontSize="xs" color="gray.700">
                        <b>Admitted:</b> {fmtDate(admittedAt)}
                      </Text>
                      <Text fontSize="xs" color="gray.700">
                        <b>Discharged:</b> {fmtDate(a.dischargedAt)}
                      </Text>
                      <Text fontSize="xs" color="gray.700">
                        <b>Ward/Bed:</b>{" "}
                        {[a.ward, a.bed].filter(Boolean).join(" / ") || "-"}
                      </Text>
                    </Box>

                    <Stack
                      mt={3}
                      spacing={2}
                      direction={{ base: "column", sm: "row" }}
                    >
                      <Button
                        size="sm"
                        variant="outline"
                        width={{ base: "100%", sm: "auto" }}
                        onClick={() => navigate(`/admissions/${a._id}`)}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        width={{ base: "100%", sm: "auto" }}
                        onClick={() =>
                          navigate(`/appointments?patient=${a.patientId}`)
                        }
                      >
                        Appointment(s)
                      </Button>
                      {a.status === "admitted" && (
                        <Button
                          size="sm"
                          colorScheme="red"
                          width={{ base: "100%", sm: "auto" }}
                          onClick={() =>
                            handleOpenDischarge(a._id, a.patientId)
                          }
                        >
                          Discharge
                        </Button>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </>
      )}

      {/* Discharge modal */}
      {dischargeTarget && (
        <DischargeConfirm
          isOpen={!!dischargeTarget}
          onClose={() => setDischargeTarget(null)}
          admissionId={dischargeTarget.id}
          patientId={dischargeTarget.patientId}
        />
      )}
    </Box>
  );
}
